import React, { useEffect, useRef } from 'react';
import { getCursorFromDocumentIndex } from 'gatsby-source-prismic-graphql';
import styled from "@emotion/styled";
import dimensions from 'styles/dimensions';
import colors from "styles/colors";
import { Inner, Wrap } from 'styles/structure';
import { graphql } from 'gatsby';
import Layout from 'components/Layout';
import SEO from 'components/SEO';
import Button from 'components/_ui/Button';
import { Rubric } from 'styles/typography';
import Fade from "react-reveal/Fade";

const MediaHeader = styled.header`
  padding: 6.4rem 0 4.8rem;
  text-align: center;

  @media(min-width: ${dimensions.tabletLandscapeUp}px) {
    padding: 8rem 0;
  }
`;

const MediaList = styled.ul`
  margin: 0 auto;
  max-width: 60rem;
  
  li {    
    & + * {
      margin-top: 4rem;
    }
  }
  
  & + * {
    margin-top: 5.6rem;
  }
  
  @media(min-width: ${dimensions.tabletLandscapeUp}px) {
    max-width: none;  
  }
`;

const MediaLink = styled.a`
  display: block;
  
  h4 {
    transition: color 0.12s ease-in-out;
  }
  
  &:hover {
    h4 {
      color: ${colors.red500};
    }  
  }
  
  @media(min-width: ${dimensions.tabletLandscapeUp}px) {
    display: flex;
  }  
`;

const MediaImage = styled.div`
  position: relative;
  overflow: hidden;
  
  & + * {
    margin-top: 1.6rem;
  }
  
  &::after {
    content: '';
    display: block;
    width: 100%;
    padding-bottom: 66.6667%;
  }
  
  img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  @media(min-width: ${dimensions.tabletLandscapeUp}px) {
    width: 37.6rem;
    flex: 1 1 37.6rem;
    margin-right: 4.8rem;
  }
`;

const MediaContent = styled.div`
  @media(min-width: ${dimensions.tabletLandscapeUp}px) {
    flex: 1 1 66.66667%;
  }
`;

const MediaButton = styled.div`
  text-align: center;
  margin: 5.6rem 0 8rem;
  
  @media(min-width: ${dimensions.tabletLandscapeUp}px) {
    margin: 8rem 0 10.4rem;
  }
`;

export default props => {
  const landing = props.data.prismic.allIn_the_medias.edges[0].node;

  const limit = 8;
  const didMountRef = useRef(false);
  const [page, setPage] = React.useState(-1);
  const [data, setData] = React.useState(props.data.prismic.allArticles);
  const [articles, setArticles] = React.useState(data.edges);

  const onLoadMore = () => {
    setPage(page + limit);
  };

  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }

    props.prismic
      .load({ variables: { after: getCursorFromDocumentIndex(page) } })
      .then(res => {
        setData(res.data.allArticles);
        setArticles(a => a.concat(res.data.allArticles.edges));
      });
  }, [props, page]);

  return (
    <Layout>
      <SEO
        title={landing.social_title || landing.heading || 'In the Media'}
        description={landing.social_description ? landing.social_description : null}
        image={landing.social_image ? landing.social_image.url : null}
      />

      <Inner>
        <Wrap>
          <MediaHeader>
            <h2>{landing.heading || 'In the Media'}</h2>
          </MediaHeader>

          <MediaList>
            {articles.map(({ node, i }) => (
              <Fade bottom distance='2.4rem'>
                <li key={i}>
                  <MediaLink href={node.destination} target='_blank' rel='noreferrer nofollow'>
                    {node.feature_image && (
                      <MediaImage>
                        <img src={node.feature_image.url} alt={node.feature_image.alt} loading="lazy" />
                      </MediaImage>
                    )}

                    <MediaContent>
                      {node.publication && <Rubric>{node.publication}</Rubric>}
                      <h4>{node.title}</h4>
                    </MediaContent>
                  </MediaLink>
                </li>
              </Fade>
            ))}
          </MediaList>

          <MediaButton>
            {data.pageInfo.hasNextPage && (
              <Button
                disabled={!data.pageInfo.hasNextPage}
                onClick={onLoadMore}
                className={!data.pageInfo.hasNextPage && 'is-disabled'}
              >
                <span>Load More</span>
              </Button>
            )}
          </MediaButton>
        </Wrap>
      </Inner>
    </Layout>
  );
};

export const query = graphql`
  query getArticles($first: Int = 8, $after: String, $before: String) {
    prismic {
      allIn_the_medias {
        edges {
          node {
            heading
            social_description
            social_image
            social_title
          }
        }
      }
      allArticles(first: $first, after: $after, before: $before, sortBy: publish_date_DESC) {
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
        edges {
          node {
            publish_date
            destination
            feature_image
            publication
            title
            _meta {
              id
              uid
            }
          }
        }
      }
    }
  }
`;
